import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { obtenerOrdenCompra } from "../services/ordenesCompraService";
import { obtenerEmpresa } from "../services/empresaService";
import { getLogoUrl, getEmpresaId } from "../utils/logoUtils";
import "../components/FacturaPrint.css";

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (s) => {
  if (!s) return "";
  const parts = s.includes("/") ? s.split("/") : s.split("-");
  if (parts.length !== 3) return s;
  const [y, m, d] = parts;
  return `${d}/${m}/${y}`;
};

// número a letras en español (para el "Son:")
const UNIDADES  = ["", "UN", "DOS", "TRES", "CUATRO", "CINCO", "SEIS", "SIETE", "OCHO", "NUEVE",
                   "DIEZ", "ONCE", "DOCE", "TRECE", "CATORCE", "QUINCE",
                   "DIECISÉIS", "DIECISIETE", "DIECIOCHO", "DIECINUEVE"];
const DECENAS   = ["", "", "VEINTE", "TREINTA", "CUARENTA", "CINCUENTA",
                   "SESENTA", "SETENTA", "OCHENTA", "NOVENTA"];
const CENTENAS  = ["", "CIENTO", "DOSCIENTOS", "TRESCIENTOS", "CUATROCIENTOS", "QUINIENTOS",
                   "SEISCIENTOS", "SETECIENTOS", "OCHOCIENTOS", "NOVECIENTOS"];

function centenasALetras(n) {
  if (n === 100) return "CIEN";
  const c = Math.floor(n / 100);
  const resto = n % 100;
  const cent = c ? CENTENAS[c] : "";
  let dec = "";
  if (resto < 20) {
    dec = UNIDADES[resto];
  } else {
    const d = Math.floor(resto / 10);
    const u = resto % 10;
    dec = u ? `${DECENAS[d]} Y ${UNIDADES[u]}` : DECENAS[d];
    if (d === 2 && u) dec = `VEINTI${UNIDADES[u]}`;
  }
  return [cent, dec].filter(Boolean).join(" ");
}

function intToWords(n) {
  if (n === 0) return "CERO";
  if (n < 0)   return `MENOS ${intToWords(-n)}`;
  if (n < 1000) return centenasALetras(n);
  if (n < 2000) return `MIL ${centenasALetras(n % 1000)}`.trim();
  if (n < 1000000) {
    const miles = Math.floor(n / 1000);
    const resto = n % 1000;
    return [centenasALetras(miles) + " MIL", resto ? centenasALetras(resto) : ""].filter(Boolean).join(" ");
  }
  const mill  = Math.floor(n / 1000000);
  const resto = n % 1000000;
  const millStr = mill === 1 ? "UN MILLÓN" : `${intToWords(mill)} MILLONES`;
  return [millStr, resto ? intToWords(resto) : ""].filter(Boolean).join(" ");
}

function montoALetras(total, moneda) {
  const [intPart, decPart = "00"] = Math.abs(Number(total)).toFixed(2).split(".");
  const cents    = decPart.padEnd(2, "0").slice(0, 2);
  const palabras = intToWords(parseInt(intPart));
  const currency = moneda === "D" ? "DÓLARES" : "PESOS";
  const sufijo   = moneda === "D" ? "USD." : "M.N.";
  return `${palabras} ${currency} ${cents}/100 ${sufijo}`;
}

// obtiene campos de empresa de localStorage o API
const mapEmpresa = (e) => {
  if (!e || typeof e !== "object") return {};
  const n  = Object.fromEntries(Object.entries(e).map(([k, v]) => [k.toLowerCase(), v]));
  const p  = (...ks) => { const f = ks.find(k => n[k] != null); return f ? n[f] : ""; };
  return {
    nombre:    p("nombre_empresa", "nombre", "razon_social"),
    telefono:  p("telefonos_empresa", "telefono"),
    email:     p("email_empresa", "email"),
    logo:      getLogoUrl(getEmpresaId()),
  };
};

// ── estilos ───────────────────────────────────────────────────────────────────
const TH = { background: "#1a3e72", color: "#fff", padding: "5px 8px", fontSize: 10, fontWeight: 700, textAlign: "left", border: "none" };
const THR = { ...TH, textAlign: "right" };
const TD  = { padding: "5px 8px", fontSize: 10, borderBottom: "1px solid #e5e7eb", verticalAlign: "top" };
const TDR = { ...TD, textAlign: "right", fontVariantNumeric: "tabular-nums" };

// ── componente ────────────────────────────────────────────────────────────────
export default function OrdenCompraPrintPage() {
  const { id } = useParams();
  const empresaId = Number(localStorage.getItem("empresaId") || 1);

  const [oc,      setOc]      = useState(null);
  const [empresa, setEmpresa] = useState({});
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  useEffect(() => {
    (async () => {
      try {
        const d = await obtenerOrdenCompra(Number(id), empresaId);
        setOc(d);
      } catch {
        setError("No se pudo cargar la orden de compra.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, empresaId]);

  useEffect(() => {
    const fromLS = JSON.parse(localStorage.getItem("empresa") || "null");
    if (fromLS) { setEmpresa(mapEmpresa(fromLS)); return; }
    if (!empresaId) return;
    obtenerEmpresa(empresaId)
      .then(resp => {
        const raw = resp?.data?.data ?? resp?.data ?? resp ?? null;
        const obj = Array.isArray(raw) ? raw[0] : raw;
        if (obj) setEmpresa(mapEmpresa(obj));
      })
      .catch(() => {});
  }, [empresaId]);

  useEffect(() => {
    if (!loading && oc) {
      const t = setTimeout(() => window.print(), 500);
      return () => clearTimeout(t);
    }
  }, [loading, oc]);

  if (loading) return <div className="print-page-pad">Cargando…</div>;
  if (error)   return <div className="print-page-pad print-error">{error}</div>;
  if (!oc)     return <div className="print-page-pad">No se encontró la OC.</div>;

  const monedaLabel = oc.moneda === "D" ? "USD" : "MXN";
  const hayDescuento = Number(oc.descuento) > 0;

  return (
    <div className="print-page">
      <main className="print-doc">

        {/* ── ENCABEZADO ── */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 10 }}>

          {/* Logo + proveedor */}
          <div style={{ minWidth: 120 }}>
            <img src={getLogoUrl(getEmpresaId())} alt="Logo" style={{ width: 110, objectFit: "contain" }} onError={(e) => { e.target.style.display = "none"; }} />
            <div style={{ fontSize: 11, fontWeight: 700, color: "#1a1a2e", marginTop: 4 }}>
              {oc.nombreProv}
            </div>
          </div>

          {/* Título */}
          <div style={{ flex: 1, border: "2px solid #1a3e72", borderRadius: 4, padding: "8px 14px", textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1a3e72" }}>
              {empresa.nombre || "LMD Integradora de Tecnología"}
            </div>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#1a3e72", letterSpacing: 2, marginTop: 4 }}>
              ORDEN DE COMPRA
            </div>
          </div>

          {/* Fecha / Folio */}
          <div style={{ border: "1px solid #c9d4e8", borderRadius: 4, minWidth: 120, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderBottom: "1px solid #c9d4e8" }}>
              <div style={{ padding: "4px 8px", fontWeight: 700, fontSize: 11, color: "#1a3e72", borderRight: "1px solid #c9d4e8" }}>
                Fecha
              </div>
              <div style={{ padding: "4px 8px", fontWeight: 700, fontSize: 11, color: "#1a3e72" }}>
                Folio
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
              <div style={{ padding: "4px 8px", fontSize: 11, borderRight: "1px solid #c9d4e8" }}>
                {fmtDate(oc.fecha)}
              </div>
              <div style={{ padding: "4px 8px", fontSize: 16, fontWeight: 900, color: "#1a3e72" }}>
                {oc.ordenCompra}
              </div>
            </div>
          </div>
        </div>

        {/* ── TABLA DE ARTÍCULOS ── */}
        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 12 }}>
          <colgroup>
            <col style={{ width: "15%" }} />
            <col />
            <col style={{ width: "10%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "13%" }} />
          </colgroup>
          <thead>
            <tr>
              <th style={TH}>Artículo</th>
              <th style={TH}>Descripción</th>
              <th style={{ ...THR }}>Cantidad</th>
              <th style={THR}>P. Unitario</th>
              <th style={THR}>Importe</th>
            </tr>
          </thead>
          <tbody>
            {oc.detalle.map((d, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f8faff" }}>
                <td style={{ ...TD, fontWeight: 600, fontSize: 10, wordBreak: "break-word" }}>{d.articulo}</td>
                <td style={TD}>{d.descripcion}</td>
                <td style={TDR}>{fmt(d.cantidad)}</td>
                <td style={TDR}>${fmt(d.precioUnitario)}</td>
                <td style={TDR}>${fmt(d.importe)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── NOTAS + TOTALES ── */}
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 16 }}>

          {/* Notas adicionales */}
          <div style={{ flex: 1, fontSize: 10, color: "#333", whiteSpace: "pre-line", lineHeight: 1.6 }}>
            {oc.notasAdicionales || ""}
          </div>

          {/* Totales */}
          <div style={{ minWidth: 220 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td style={{ padding: "3px 8px", fontSize: 10, fontWeight: 600 }}>Subtotal</td>
                  <td style={{ padding: "3px 8px", fontSize: 10, textAlign: "right",
                               background: "#e8edf8", fontVariantNumeric: "tabular-nums", minWidth: 80 }}>
                    {fmt(oc.subtotalAntesDDesc ?? oc.subtotal)}
                  </td>
                </tr>
                {hayDescuento && (
                  <>
                    <tr>
                      <td style={{ padding: "3px 8px", fontSize: 10, fontWeight: 600 }}>Descuento</td>
                      <td style={{ padding: "3px 8px", fontSize: 10, textAlign: "right", background: "#e8edf8", fontVariantNumeric: "tabular-nums" }}>
                        {fmt(oc.descuento)}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: "3px 8px", fontSize: 10, fontWeight: 600 }}>Subtotal</td>
                      <td style={{ padding: "3px 8px", fontSize: 10, textAlign: "right", background: "#e8edf8", fontVariantNumeric: "tabular-nums" }}>
                        {fmt(oc.subtotal)}
                      </td>
                    </tr>
                  </>
                )}
                <tr>
                  <td style={{ padding: "3px 8px", fontSize: 10, fontWeight: 600 }}>Iva</td>
                  <td style={{ padding: "3px 8px", fontSize: 10, textAlign: "right", background: "#e8edf8", fontVariantNumeric: "tabular-nums" }}>
                    {fmt(oc.iva)}
                  </td>
                </tr>
                <tr>
                  <td style={{ padding: "3px 8px", fontSize: 11, fontWeight: 800, color: "#1a3e72" }}>Total</td>
                  <td style={{ padding: "3px 8px", fontSize: 11, textAlign: "right", fontWeight: 800,
                               background: "#e8edf8", color: "#1a3e72", fontVariantNumeric: "tabular-nums" }}>
                    {fmt(oc.total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* ── SON EN LETRAS ── */}
        <div style={{ fontSize: 10, marginBottom: 16 }}>
          <span style={{ fontWeight: 700 }}>Son: </span>
          {montoALetras(oc.total, oc.moneda)}
          {" "}
          <span style={{ fontWeight: 600 }}>({monedaLabel})</span>
        </div>

        {/* ── PIE ── */}
        <div style={{ borderTop: "1px solid #c9d4e8", paddingTop: 8, textAlign: "right", fontSize: 10, color: "#555" }}>
          {empresa.telefono && <div style={{ fontWeight: 700 }}>Teléfono: {empresa.telefono}</div>}
          {empresa.email    && <div>{empresa.email}</div>}
        </div>

      </main>
    </div>
  );
}
